
import React, { useState, useRef } from 'react';
import { 
  Upload, 
  FileText, 
  CheckCircle2, 
  AlertTriangle, 
  Loader2, 
  ShieldAlert, 
  ShieldCheck,
  Search,
  XCircle,
  FileCheck
} from 'lucide-react';
import Modal from './Modal';
import { ClaudeService, ComplianceResult } from '../services/claudeService';
import { useToast } from '../context/ToastContext';

interface HireComplianceModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultFile?: File | null;
  expectedHirerName?: string; // Expected hirer name to validate against
  expectedVRM?: string; // Expected VRM to validate against
}

const HireComplianceModal: React.FC<HireComplianceModalProps> = ({
  isOpen,
  onClose,
  defaultFile,
  expectedHirerName,
  expectedVRM
}) => {
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(defaultFile || null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<ComplianceResult | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const runAudit = async () => {
    if (!file) return;
    setIsAnalyzing(true);
    try {
      const data = await ClaudeService.analyzeHireAgreement(file, {
        expectedHirerName,
        expectedVRM
      });
      setResult(data);
      if (data.validation_warnings && data.validation_warnings.length > 0) {
        addToast("Analysis complete with warnings", "warning");
      } else {
        addToast("Analysis complete", "success");
      }
    } catch (err) {
      addToast("Failed to analyze document", "error");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20';
    if (score >= 60) return 'text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20';
    return 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { onClose(); setFile(null); setResult(null); }}
      title="Preview Agreement Compliance"
      maxWidth="max-w-2xl"
    >
      <div className="p-6">

        {/* Info Banner - This is a preview tool */}
        <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-bold text-amber-900 dark:text-amber-100 text-sm">Preview Tool Only</h4>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              This is a <strong>preview tool</strong> to check agreements before uploading.
              The actual compliance audit runs <strong>automatically</strong> when tickets are created and matched to hires.
            </p>
          </div>
        </div>

        {/* State 1: Upload */}
        {!result && (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-900 rounded-lg p-4 flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h4 className="font-bold text-blue-900 dark:text-blue-100">Preview Compliance Check</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Upload a hire agreement to preview TMA 2004 & PoFA 2012 compliance.
                  We scan for liability clauses, signatures, and required vehicle details.
                </p>
              </div>
            </div>

            <div 
              className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-10 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept=".pdf,.jpg,.png" 
                onChange={handleFileChange}
              />
              {file ? (
                <div className="text-center">
                  <FileText className="w-12 h-12 text-primary-600 dark:text-primary-400 mx-auto mb-3" />
                  <p className="font-bold text-slate-800 dark:text-white">{file.name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{(file.size / 1024).toFixed(0)} KB</p>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-12 h-12 text-slate-400 dark:text-slate-500 mx-auto mb-3" />
                  <p className="font-bold text-slate-700 dark:text-slate-300">Click to upload document</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">PDF or Image formats supported</p>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={runAudit}
                disabled={!file || isAnalyzing}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isAnalyzing ? <Loader2 className="animate-spin" /> : <Search size={18} />}
                {isAnalyzing ? 'Analyzing Agreement...' : 'Preview Compliance'}
              </button>
            </div>
          </div>
        )}

        {/* State 2: Results */}
        {result && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            
            {/* Header Score */}
            <div className="flex items-center gap-6">
              <div className={`w-24 h-24 rounded-full flex flex-col items-center justify-center border-4 ${getScoreColor(result.score).replace('bg-', 'border-').replace('text-', 'text-')}`}>
                <span className="text-2xl font-bold dark:text-white">{result.score}</span>
                <span className="text-[10px] uppercase font-bold dark:text-slate-300">Score</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-slate-800 dark:text-white">{result.status}</h3>
                  {result.status === 'Compliant' && <CheckCircle2 className="text-green-500" />}
                  {result.status === 'At Risk' && <AlertTriangle className="text-amber-500" />}
                  {result.status === 'Non-Compliant' && <XCircle className="text-red-500" />}
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{result.summary}</p>
              </div>
            </div>

            {/* Validation Warnings (if any) */}
            {result.validation_warnings && result.validation_warnings.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h4 className="text-sm font-bold text-red-900 dark:text-red-100 mb-2 flex items-center gap-2">
                  <XCircle size={18} className="text-red-600 dark:text-red-400" /> Validation Warnings
                </h4>
                <ul className="space-y-2">
                  {result.validation_warnings.map((warning, i) => (
                    <li key={i} className="text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">⚠️</span>
                      <span>{warning}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Extracted Data */}
            {result.extracted_data && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
                <h4 className="text-xs font-bold text-blue-900 dark:text-blue-100 uppercase tracking-wide mb-3">Extracted Information</h4>
                <div className="space-y-2">
                  {result.extracted_data.hirer_name && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-700 dark:text-blue-300 font-semibold">Hirer Name:</span>
                      <span className="text-xs text-blue-900 dark:text-blue-100 font-mono bg-white dark:bg-blue-950 px-2 py-1 rounded">{result.extracted_data.hirer_name}</span>
                      {expectedHirerName && result.extracted_data.hirer_name.toLowerCase().includes(expectedHirerName.toLowerCase()) && (
                        <CheckCircle2 size={14} className="text-green-500" />
                      )}
                    </div>
                  )}
                  {result.extracted_data.vrm && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-700 dark:text-blue-300 font-semibold">VRM:</span>
                      <span className="text-xs text-blue-900 dark:text-blue-100 font-mono bg-white dark:bg-blue-950 px-2 py-1 rounded">{result.extracted_data.vrm}</span>
                      {expectedVRM && result.extracted_data.vrm.replace(/\s/g, '').toUpperCase() === expectedVRM.replace(/\s/g, '').toUpperCase() && (
                        <CheckCircle2 size={14} className="text-green-500" />
                      )}
                    </div>
                  )}
                  {result.extracted_data.hire_start && result.extracted_data.hire_end && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-blue-700 dark:text-blue-300 font-semibold">Hire Period:</span>
                      <span className="text-xs text-blue-900 dark:text-blue-100 font-mono bg-white dark:bg-blue-950 px-2 py-1 rounded">
                        {new Date(result.extracted_data.hire_start).toLocaleDateString('en-GB')} - {new Date(result.extracted_data.hire_end).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                  )}
                  {result.extracted_data.liability_clause_text && (
                    <div className="mt-3">
                      <span className="text-xs text-blue-700 dark:text-blue-300 font-semibold block mb-1">Liability Clause:</span>
                      <div className="text-xs text-blue-900 dark:text-blue-100 bg-white dark:bg-blue-950 p-3 rounded border border-blue-200 dark:border-blue-800 italic">
                        "{result.extracted_data.liability_clause_text}"
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Checklist */}
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
              <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Required Elements (TMA 2004)</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'VRM Identified', valid: result.detected_fields.vrm },
                  { label: 'Hire Dates', valid: result.detected_fields.dates },
                  { label: 'Statement of Liability', valid: result.detected_fields.liability_statement },
                  { label: 'Hirer Signature', valid: result.detected_fields.signature },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white dark:bg-slate-900 p-2 rounded border border-slate-100 dark:border-slate-800">
                    {item.valid ? <CheckCircle2 size={16} className="text-green-500" /> : <XCircle size={16} className="text-red-500" />}
                    <span className={`text-sm ${item.valid ? 'text-slate-700 dark:text-slate-300' : 'text-red-700 dark:text-red-400 font-medium'}`}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            {(result.missing_clauses.length > 0 || result.recommendations.length > 0) && (
              <div>
                <h4 className="text-sm font-bold text-slate-800 dark:text-white mb-3 flex items-center gap-2">
                  <FileCheck size={18} className="text-primary-600 dark:text-primary-400" /> Areas for Improvement
                </h4>
                <ul className="space-y-3">
                  {result.missing_clauses.map((clause, i) => (
                    <li key={`missing-${i}`} className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-lg">
                      <ShieldAlert className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-red-800 dark:text-red-300">Missing: {clause}</p>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">This clause is critical for successful liability transfer.</p>
                      </div>
                    </li>
                  ))}
                  {result.recommendations.map((rec, i) => (
                    <li key={`rec-${i}`} className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/50 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800 dark:text-amber-300">{rec}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Next Steps Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-2">What happens next?</h4>
              <p className="text-xs text-blue-700 dark:text-blue-300">
                When you upload this hire agreement, the system will automatically run a full compliance audit when matching tickets.
                High-confidence matches (≥85%) will be auto-assigned to drivers.
              </p>
            </div>

            <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
              <button 
                onClick={() => { setFile(null); setResult(null); }}
                className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-medium mr-2"
              >
                Scan Another
              </button>
              <button 
                onClick={onClose}
                className="px-6 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-bold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default HireComplianceModal;
